import { Box, Stack } from "@mui/material";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";

import type { SKUStoreStatusBadgeItem } from "@/features/SKUStore/components/Status/statusBadgeItems";
import type { SxProps, Theme } from "@mui/material/styles";

import {
  SKUStoreStatusBadge,
  SKUStoreStatusBadgeList,
} from "@/features/SKUStore/components/Status/SKUStoreStatusBadges";
import { fitStatusBadges } from "@/features/SKUStore/components/Status/statusBadgeItems";

type Measurement = {
  signature: string;
  widths: number[];
  overflowWidth: number;
  gap: number;
};

const baseSx: SxProps<Theme> = {
  flexWrap: "nowrap",
  alignItems: "center",
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  position: "relative",
};

/** Laid out off-screen for measuring only; never seen, focused, or read. */
const measureLayerSx: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  display: "flex",
  visibility: "hidden",
  pointerEvents: "none",
  whiteSpace: "nowrap",
};

/**
 * Status chips on a single line: whatever does not fit folds into a "+N" chip
 * whose tooltip names the hidden chips, so a card never grows taller.
 *
 * Chip widths are measured once per set of chips, in a hidden layer that is
 * removed straight after; the row refits as its own width changes.
 */
export function SKUStoreStatusBadgeRow({
  items,
  onClick,
  sx,
  badgeRadius,
}: {
  items: SKUStoreStatusBadgeItem[];
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  sx?: SxProps<Theme>;
  /** Corner radius in px for every chip, "+N" included; pills when omitted. */
  badgeRadius?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [measurement, setMeasurement] = useState<Measurement | null>(null);
  const [available, setAvailable] = useState(0);

  const signature = items.map((item) => `${item.key}:${item.label}`).join("|");
  const needsMeasure = measurement?.signature !== signature;

  useLayoutEffect(() => {
    const layer = measureRef.current;
    const container = containerRef.current;
    if (!needsMeasure || layer == null || container == null) return;
    const chips = Array.from(layer.children);
    setMeasurement({
      signature,
      widths: chips
        .slice(0, -1)
        .map((chip) => chip.getBoundingClientRect().width),
      overflowWidth: chips.at(-1)?.getBoundingClientRect().width ?? 0,
      gap: parseFloat(getComputedStyle(container).columnGap) || 0,
    });
  }, [needsMeasure, signature]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (container == null) return;
    const update = () => setAvailable(container.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Widths taken before the web font loads are too narrow; measure again.
  useEffect(() => {
    let cancelled = false;
    const fontsReady = (
      document as Document & { fonts?: { ready: Promise<unknown> } }
    ).fonts?.ready;
    fontsReady
      ?.then(() => {
        if (!cancelled) setMeasurement(null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const hidden =
    measurement == null || needsMeasure
      ? []
      : fitStatusBadges(items, measurement.widths, {
          available,
          gap: measurement.gap,
          overflowWidth: measurement.overflowWidth,
        });
  const hiddenKeys = new Set(hidden.map((item) => item.key));

  return (
    <Stack
      ref={containerRef}
      direction="row"
      spacing={2}
      useFlexGap
      onClick={onClick}
      sx={sx == null ? baseSx : ([baseSx, sx] as SxProps<Theme>)}
    >
      <SKUStoreStatusBadgeList
        items={items.filter((item) => !hiddenKeys.has(item.key))}
        radius={badgeRadius}
      />
      {hidden.length > 0 ? (
        <SKUStoreStatusBadge
          tone="neutral"
          radius={badgeRadius}
          label={`+${String(hidden.length)}`}
          tooltip={hidden.map((item) => item.label).join(", ")}
        />
      ) : null}
      {needsMeasure ? (
        <Box ref={measureRef} aria-hidden inert sx={measureLayerSx}>
          {items.map((item) => (
            <Box key={item.key} component="span" sx={{ display: "flex" }}>
              <SKUStoreStatusBadgeList items={[item]} radius={badgeRadius} />
            </Box>
          ))}
          {/* The widest "+N" a row of up to nine chips can need. */}
          <Box component="span" sx={{ display: "flex" }}>
            <SKUStoreStatusBadge
              tone="neutral"
              label="+9"
              radius={badgeRadius}
            />
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
}
